import { useState, useCallback } from "react";
import { joyControlMappingService } from "../services/joyControlMappingService";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";

export const useJoyControlMappingManagement = () => {
  const { authFetch } = useAuth();
  const { notifySuccess, notifyError } = useNotification();

  const [mappingsData, setMappingsData] = useState({
    page: 1,
    per_page: 100,
    total: 0,
    results: [],
  });
  const [loading, setLoading] = useState(false);

  const loadMappings = useCallback(
    async (page = 1, perPage = 100) => {
      try {
        setLoading(true);
        const response = await joyControlMappingService.getAll(authFetch, page, perPage);
        setMappingsData(response.data);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        notifyError(`Failed to load joy control mappings: ${errorMessage}`);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, notifyError],
  );

  const createMapping = useCallback(
    async (mappingData) => {
      try {
        const response = await joyControlMappingService.create(authFetch, mappingData);
        notifySuccess("Joy control mapping created successfully");
        await loadMappings(mappingsData.page, mappingsData.per_page);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        notifyError(`Failed to create joy control mapping: ${errorMessage}`);
        throw error;
      }
    },
    [authFetch, notifySuccess, notifyError, loadMappings, mappingsData.page, mappingsData.per_page],
  );

  const updateMapping = useCallback(
    async (mappingId, updateData) => {
      try {
        const response = await joyControlMappingService.update(authFetch, mappingId, updateData);
        notifySuccess("Joy control mapping updated successfully");
        await loadMappings(mappingsData.page, mappingsData.per_page);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        notifyError(`Failed to update joy control mapping: ${errorMessage}`);
        throw error;
      }
    },
    [authFetch, notifySuccess, notifyError, loadMappings, mappingsData.page, mappingsData.per_page],
  );

  const deleteMapping = useCallback(
    async (mappingId) => {
      try {
        await joyControlMappingService.delete(authFetch, mappingId);
        notifySuccess("Joy control mapping deleted successfully");
        await loadMappings(mappingsData.page, mappingsData.per_page);
        return true;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        notifyError(`Failed to delete joy control mapping: ${errorMessage}`);
        return false;
      }
    },
    [authFetch, notifySuccess, notifyError, loadMappings, mappingsData.page, mappingsData.per_page],
  );

  return {
    mappingsData,
    loading,
    loadMappings,
    createMapping,
    updateMapping,
    deleteMapping,
    pagination: {
      current: mappingsData.page,
      pageSize: mappingsData.per_page,
      total: mappingsData.total,
      onChange: (page, pageSize) => loadMappings(page, pageSize),
    },
  };
};
